import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, CheckCheck } from "lucide-react";
import { api } from "@/lib/api";
import { fmtDate } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";

export const NotificationBell = ({ dark = false }) => {
  const { user } = useAuth();
  const [data, setData] = useState({ items: [], unread: 0 });
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const timer = useRef(null);

  const load = () => api.get("/notifications").then((r) => setData(r.data)).catch(() => {});

  useEffect(() => {
    if (!user) return undefined;
    load();
    timer.current = setInterval(load, 30000);
    return () => clearInterval(timer.current);
  }, [user]);

  if (!user) return null;

  const openItem = async (n) => {
    if (!n.is_read) {
      await api.post(`/notifications/${n.id}/read`).catch(() => {});
      load();
    }
    setOpen(false);
    if (n.order_id) navigate(user.role === "client" ? "/commandes" : "/admin/commandes");
  };

  const markAll = async () => {
    await api.post("/notifications/read-all").catch(() => {});
    load();
  };

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={`relative grid h-10 w-10 place-items-center rounded-xl border transition-colors ${
          dark ? "border-slate-700 text-slate-200 hover:bg-slate-800" : "border-slate-200 text-slate-700 hover:bg-slate-50"
        }`}
        data-testid="notification-bell"
      >
        <Bell className="h-5 w-5" />
        {data.unread > 0 && (
          <span
            className="font-num absolute -right-1 -top-1 grid h-5 min-w-5 place-items-center rounded-full bg-rose-600 px-1 text-[10px] font-bold text-white"
            data-testid="notification-unread-badge"
          >
            {data.unread}
          </span>
        )}
      </button>

      {open && (
        <div
          className="absolute right-0 top-12 z-50 w-80 max-w-[85vw] overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-xl"
          data-testid="notification-panel"
        >
          <div className="flex items-center justify-between border-b border-slate-100 px-3 py-2">
            <p className="text-sm font-bold text-slate-900">Notifications</p>
            <button type="button" onClick={markAll} className="flex items-center gap-1 text-xs font-semibold text-brand" data-testid="notification-mark-all">
              <CheckCheck className="h-3.5 w-3.5" /> Tout lire
            </button>
          </div>
          <div className="max-h-80 overflow-y-auto">
            {data.items.length === 0 && <p className="p-4 text-sm text-slate-500">Aucune notification.</p>}
            {data.items.map((n) => (
              <button
                key={n.id}
                type="button"
                onClick={() => openItem(n)}
                className={`block w-full border-b border-slate-100 px-3 py-2.5 text-left hover:bg-slate-50 ${n.is_read ? "" : "bg-brand-light/50"}`}
                data-testid={`notification-item-${n.id}`}
              >
                <p className="text-sm font-semibold text-slate-900">{n.title}</p>
                <p className="mt-0.5 text-xs text-slate-600">{n.message}</p>
                <p className="mt-1 text-[10px] text-slate-400">{fmtDate(n.created_at)}</p>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
